import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchHome } from "../api/home";
import { fetchHousehold } from "../api/household";
import { fetchSchoolAgendaDates } from "../api/regionalCalendar";
import { fetchScheduleItems, patchScheduleItem } from "../api/scheduleItems";
import { patchSchoolEmailEventOverride } from "../api/schoolEmails";
import { FORGET_COPY } from "../lib/extractionForget";
import { originFromSavedItem } from "../lib/heuteScheduleMerge";
import { readHomeSnapshot, writeHomeSnapshot } from "../lib/homeSnapshot";
import {
  CHAT_HOME_AGENDA_DAYS,
  CHAT_SCHEDULE_RECALL_DAYS,
  CHAT_SCHEDULE_RECALL_EXTEND_DAYS,
  mergeScheduleItemsForChat,
  scheduleItemsWithinDays,
} from "../lib/chatScheduleRecall";
import { mergeSchoolDatesForAgenda } from "../lib/schoolAgendaDates";
import { itemShownOnCalendar } from "../lib/occasionMatch";
import { addDaysYmd } from "../lib/heuteScheduleBuckets";
import { todayYmdZurich } from "../lib/schoolAttention";
import {
  omitDoneScheduleSources,
  patchRemindScheduleSources,
  restoreDoneScheduleSources,
} from "../lib/heuteScheduleMerge";

const UNDO_MS = 8000;

/**
 * Shared household agenda for chat cards (mail, photo, manual).
 * @param {{ enabled?: boolean, userId?: string|null }} [opts]
 */
export default function useHouseholdAgenda({ enabled = true, userId = null } = {}) {
  const [savedItems, setSavedItems] = useState([]);
  const [schoolDates, setSchoolDates] = useState([]);
  const [nearHolidays, setNearHolidays] = useState([]);
  const [fullSaved, setFullSaved] = useState(null);
  const [householdLinked, setHouseholdLinked] = useState(false);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [hasMail, setHasMail] = useState(null);
  const [greeting, setGreeting] = useState(null);
  const [homeSettled, setHomeSettled] = useState(false);
  const hasItemsRef = useRef(false);
  const [seenUserId, setSeenUserId] = useState(undefined);
  const fullPromiseRef = useRef(null);
  const [canPersist, setCanPersist] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [undo, setUndo] = useState(null);
  const inFlightKeys = useRef(new Set());
  const undoTimerRef = useRef(null);

  const clearUndoTimer = () => {
    if (undoTimerRef.current) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  };

  const dismissUndo = useCallback(() => {
    clearUndoTimer();
    setUndo(null);
  }, []);

  if (userId !== seenUserId) {
    setSeenUserId(userId);
    const snap = userId ? readHomeSnapshot(userId) : null;
    hasItemsRef.current = Boolean(snap?.items?.length);
    setSavedItems(snap?.items || []);
    setSchoolDates(snap?.schoolDates || []);
    setNearHolidays(snap?.nearHolidays || []);
    setHasMail(snap && typeof snap.hasMail === "boolean" ? snap.hasMail : null);
    setGreeting(snap?.greeting || null);
    setHouseholdLinked(false);
    setFullSaved(null);
    fullPromiseRef.current = null;
    setCanPersist(Boolean(snap?.items?.length));
    setHomeSettled(false);
    setLoading(Boolean(enabled && userId) && !snap?.items?.length);
    setError(null);
  }

  const applySchedulePayload = useCallback((payload, { silent = false } = {}) => {
    const rows = Array.isArray(payload.items) ? payload.items : [];
    hasItemsRef.current = rows.length > 0;
    setCanPersist(true);
    setSavedItems(rows);
    setHouseholdLinked(Boolean(payload.household?.linked));
    setSchoolDates(Array.isArray(payload.schoolDates) ? payload.schoolDates : []);
    setNearHolidays(Array.isArray(payload.nearHolidays) ? payload.nearHolidays : []);
    if (silent) setActionError(null);
    else setError(null);
  }, []);

  const load = useCallback(
    async ({ silent = false, isCurrent = () => true } = {}) => {
      if (!enabled || !userId) {
        if (!isCurrent()) return;
        setSavedItems([]);
        setFullSaved(null);
        fullPromiseRef.current = null;
        setLoading(false);
        hasItemsRef.current = false;
        setCanPersist(false);
        setHasMail(null);
        setGreeting(null);
        setHomeSettled(false);
        return;
      }
      if (!silent && !hasItemsRef.current) {
        setError(null);
        setLoading(true);
      }
      try {
        const home = await fetchHome();
        if (!isCurrent()) return;
        if (home) {
          applySchedulePayload({
            items: home.items,
            household: home.household,
            schoolDates: home.schoolDates,
            nearHolidays: home.nearHolidays,
          }, { silent });
          if (typeof home.hasMail === "boolean") setHasMail(home.hasMail);
          const text = String(home.greeting?.chatText || "").trim();
          setGreeting(text && home.greeting?.date && home.greeting?.kind
            ? {
                date: home.greeting.date,
                kind: home.greeting.kind,
                text,
                payload: home.greeting,
              }
            : null);
          return;
        }
        const [savedData, household, school] = await Promise.all([
          fetchScheduleItems({ days: CHAT_HOME_AGENDA_DAYS }),
          fetchHousehold().catch(() => ({ linked: false })),
          fetchSchoolAgendaDates().catch(() => ({ schoolDates: [], nearHolidays: [] })),
        ]);
        if (!isCurrent()) return;
        applySchedulePayload({
          items: savedData.items,
          household: { linked: Boolean(household?.linked || savedData.household?.linked) },
          schoolDates: school.schoolDates,
          nearHolidays: school.nearHolidays,
        }, { silent });
      } catch (err) {
        if (!isCurrent()) return;
        const message = err instanceof Error ? err.message : String(err);
        if (silent || hasItemsRef.current) {
          setActionError(message);
          return;
        }
        setSavedItems([]);
        setError(message);
      } finally {
        if (isCurrent()) {
          if (!silent) setLoading(false);
          setHomeSettled(true);
        }
      }
    },
    [applySchedulePayload, enabled, userId],
  );

  useEffect(() => {
    let active = true;
    load({ isCurrent: () => active });
    return () => {
      active = false;
    };
  }, [load]);

  useEffect(() => () => clearUndoTimer(), []);

  const rememberGreeting = useCallback((next) => {
    const text = String(next?.text || "").trim();
    if (!text || !next?.date || !next?.kind) return;
    setGreeting({
      date: next.date,
      kind: next.kind,
      text,
      payload: next.payload || null,
    });
  }, []);

  useEffect(() => {
    if (!canPersist || !userId) return;
    writeHomeSnapshot(userId, {
      items: savedItems,
      schoolDates,
      nearHolidays,
      hasMail,
      greeting,
    });
  }, [canPersist, greeting, hasMail, nearHolidays, savedItems, schoolDates, userId]);

  const ensureFull = useCallback(async () => {
    if (!fullPromiseRef.current) {
      fullPromiseRef.current = (async () => {
        const savedData = await fetchScheduleItems({ days: 365 });
        const rows = Array.isArray(savedData.items) ? savedData.items : [];
        setFullSaved(rows);
        return mergeScheduleItemsForChat(rows, { days: null });
      })().catch((err) => {
        fullPromiseRef.current = null;
        throw err;
      });
    }
    return fullPromiseRef.current;
  }, []);

  const items = useMemo(() => {
    const today = todayYmdZurich();
    const saved = mergeScheduleItemsForChat(savedItems, { days: CHAT_HOME_AGENDA_DAYS });
    return mergeSchoolDatesForAgenda(saved, schoolDates, {
      from: today,
      to: addDaysYmd(today, CHAT_HOME_AGENDA_DAYS),
      todayYmd: today,
    });
  }, [savedItems, schoolDates]);

  const fullUpcoming = useMemo(
    () => (fullSaved ? mergeScheduleItemsForChat(fullSaved, { days: null }) : []),
    [fullSaved],
  );

  const calendarTwinKeys = useMemo(() => {
    const today = todayYmdZurich();
    const events = nearHolidays.filter(
      (event) => event.startsOn <= addDaysYmd(today, CHAT_SCHEDULE_RECALL_DAYS),
    );
    return fullUpcoming
      .filter((item) =>
        itemShownOnCalendar(
          { title: item.title, date: item.date, match_key: item.match_key, child: item.child },
          events,
        ),
      )
      .map((item) => item.key);
  }, [fullUpcoming, nearHolidays]);

  const recallItems = useMemo(() => {
    const today = todayYmdZurich();
    const saved = scheduleItemsWithinDays(fullUpcoming, CHAT_SCHEDULE_RECALL_EXTEND_DAYS);
    return mergeSchoolDatesForAgenda(saved, schoolDates, {
      from: today,
      to: addDaysYmd(today, CHAT_SCHEDULE_RECALL_EXTEND_DAYS),
      todayYmd: today,
      hideCalendarTwins: true,
      calendarEvents: nearHolidays.filter(
        (event) => event.startsOn <= addDaysYmd(today, CHAT_SCHEDULE_RECALL_DAYS),
      ),
    });
  }, [fullUpcoming, schoolDates, nearHolidays]);

  const toggleRemind = useCallback((item) => {
    if (!item?.key || !item.id || inFlightKeys.current.has(item.key)) return;
    const snapshot = { savedItems };
    const next = !item.remindEnabled;
    inFlightKeys.current.add(item.key);
    setActionError(null);
    setSavedItems(patchRemindScheduleSources(snapshot, item, next).savedItems);
    void (async () => {
      try {
        await patchScheduleItem(item.id, { remind: next });
      } catch (err) {
        setSavedItems(snapshot.savedItems);
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        inFlightKeys.current.delete(item.key);
      }
    })();
  }, [savedItems]);

  const toggleDone = useCallback((item) => {
    if (!item?.key || !item.id || inFlightKeys.current.has(item.key)) return;
    const snapshot = { savedItems };
    const omittedRow = savedItems.find((row) => row.id === item.id) || null;
    inFlightKeys.current.add(item.key);
    setActionError(null);
    dismissUndo();
    setSavedItems(omitDoneScheduleSources(snapshot, item).savedItems);
    void (async () => {
      try {
        await patchScheduleItem(item.id, { done: true });
        if (omittedRow) {
          setUndo({ row: omittedRow, title: item.title });
          undoTimerRef.current = window.setTimeout(() => setUndo(null), UNDO_MS);
        }
      } catch (err) {
        setSavedItems(snapshot.savedItems);
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        inFlightKeys.current.delete(item.key);
      }
    })();
  }, [savedItems, dismissUndo]);

  const markIrrelevant = useCallback((item) => {
    if (!item?.key || !item.id || inFlightKeys.current.has(item.key)) return;
    const snapshot = { savedItems };
    const omittedRow = savedItems.find((row) => row.id === item.id) || null;
    const mail =
      originFromSavedItem(item) === "mail" &&
      item.extractionId != null &&
      Number.isInteger(item.eventIndex);
    inFlightKeys.current.add(item.key);
    setActionError(null);
    dismissUndo();
    setSavedItems(omitDoneScheduleSources(snapshot, item).savedItems);
    void (async () => {
      try {
        if (mail) {
          await patchSchoolEmailEventOverride(item.extractionId, item.eventIndex, { dismiss: true });
        } else {
          await patchScheduleItem(item.id, { done: true });
        }
        if (omittedRow) {
          setUndo({
            row: omittedRow,
            title: item.title,
            hint: FORGET_COPY.irrelevant,
            mode: mail ? "dismiss" : "done",
            extractionId: item.extractionId,
            eventIndex: item.eventIndex,
          });
          undoTimerRef.current = window.setTimeout(() => setUndo(null), UNDO_MS);
        }
      } catch (err) {
        setSavedItems(snapshot.savedItems);
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        inFlightKeys.current.delete(item.key);
      }
    })();
  }, [savedItems, dismissUndo]);

  const undoDone = useCallback(() => {
    const row = undo?.row;
    if (!row?.id || inFlightKeys.current.has(`undo-${row.id}`)) return;
    inFlightKeys.current.add(`undo-${row.id}`);
    const mode = undo?.mode;
    const extractionId = undo?.extractionId;
    const eventIndex = undo?.eventIndex;
    dismissUndo();
    setActionError(null);
    setSavedItems((current) => restoreDoneScheduleSources({ savedItems: current }, row).savedItems);
    void (async () => {
      try {
        if (mode === "dismiss" && extractionId != null && Number.isInteger(eventIndex)) {
          await patchSchoolEmailEventOverride(extractionId, eventIndex, { dismiss: false });
        } else {
          await patchScheduleItem(row.id, { done: false });
        }
      } catch (err) {
        setSavedItems((current) =>
          omitDoneScheduleSources({ savedItems: current }, { id: row.id }).savedItems,
        );
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        inFlightKeys.current.delete(`undo-${row.id}`);
      }
    })();
  }, [undo, dismissUndo]);

  const refresh = useCallback(() => load({ silent: true }), [load]);

  return {
    items,
    recallItems,
    allItems: fullUpcoming,
    calendarTwinKeys,
    ensureFull,
    householdLinked,
    hasMail,
    greeting,
    homeSettled,
    rememberGreeting,
    loading,
    error,
    actionError,
    undo,
    toggleRemind,
    toggleDone,
    markIrrelevant,
    undoDone,
    refresh,
  };
}
